import {
    Button,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Input,
    FormGroup,
    Label,
    Spinner,
} from 'reactstrap';
import '../../Daylight.css';
import Select from 'react-select';
import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../../context';
import hasPermission from '../../../../Shared/hasPermission';
import Fuse from 'fuse.js';
import debounce from 'lodash.debounce';
import { DaylightContext } from '../DaylightCodeMapper';

const BulkAddMappingModal = () => {
    const dispatch = useDispatch();
    const { practiceId, partnerId } = useContext(DaylightContext);

    const isOpen = useSelector((state) => state.daylight.isBulkAddMappingModalOpen);
    const selectedCodeIds = useSelector(
        (state) => state.daylight.selectedCodeIds,
        shallowEqual
    );
    const isBulkActionInProgress = useSelector(
        (state) => state.daylight.isBulkActionInProgress
    );

    const incomingNeoCodes = useSelector((state) => state.daylight.incomingNeoCodes);

    const permissions = useSelector(
        (state) => state.auth.permissions,
        shallowEqual
    );
    const canWriteComments = hasPermission('CodeMapsCommentsWrite', permissions);

    const [selectedMapping, setSelectedMapping] = useState(null);
    const [commentsText, setCommentsText] = useState('');
    const [inputValue, setInputValue] = useState('');
    const [filteredNeoCodes, setFilteredNeoCodes] = useState(incomingNeoCodes);

    useEffect(() => {
        const fuseOptions = {
            keys: [
                { name: 'code', weight: 1.0 },
                { name: 'label', weight: 0.5 },
            ],
            location: 50,
            distance: 200,
            threshold: 0.5,
        };

        const fuse = new Fuse(incomingNeoCodes, fuseOptions);

        const debouncedSearch = debounce((value) => {
            const results = value
                ? fuse.search(value)
                : incomingNeoCodes.map((code) => ({ item: code }));
            setFilteredNeoCodes(results.map((result) => result.item));
        }, 300);

        debouncedSearch(inputValue);
        return () => debouncedSearch.cancel();
    }, [incomingNeoCodes, inputValue]);

    const handleInputChange = (value) => {
        setInputValue(value);
    };

    const escapeRegExp = (string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    const filteredOptions = useMemo(() => {
        if (inputValue === '') {
            return [
                ...filteredNeoCodes
                    .slice(0, 50)
                    .map((x) => {
                        return { label: `[${x.code}] ${x.label}`, value: x };
                    }),
                {
                    label: 'Search to see more codes...',
                    value: null,
                    isDisabled: true,
                },
            ];
        }
        const escapedInputValue = escapeRegExp(inputValue);
        const regByInclusion = new RegExp(escapedInputValue, 'i');
        return [
            ...filteredNeoCodes
                .filter(
                    (x) =>
                        regByInclusion.test(x.label) ||
                        regByInclusion.test(x.code)
                )
                .slice(0, 100)
                .map((x) => {
                    return { label: `[${x.code}] ${x.label}`, value: x };
                }),
            {
                label: 'Search to see more codes...',
                value: null,
                isDisabled: true,
            },
        ];
    }, [inputValue, filteredNeoCodes]);

    const confirm = () => {
        const payload = {
            taxonomyCodeNodeGuid: selectedMapping?.guid,
            taxonomyCodeNodeLabel: selectedMapping?.label,
            taxonomyCodeNodeType: selectedMapping?.nodeType,
            codeName: selectedMapping?.code,
            codeType: selectedMapping?.type,
            unitCost: selectedMapping?.unitCost,
            buyingQuantity: selectedMapping?.buyingQuantity,
            sellingQuantity: selectedMapping?.sellingQuantity,
            comments:
                commentsText !== null && commentsText !== ''
                    ? commentsText
                    : null,
        };

        dispatch(
            actions.daylight.bulkAddMappingToCodes(
                practiceId,
                selectedCodeIds,
                payload,
                partnerId
            )
        );
    };

    const closeModal = () => {
        setSelectedMapping(null);
        setCommentsText('');
        setInputValue('');
        dispatch(actions.daylight.setBulkAddMappingModalOpen(false));
    };

    return (
        <Modal
            centered={true}
            isOpen={isOpen}
            className="text-light"
            style={{ minWidth: '50vw' }}>
            <ModalHeader>
                {`Add Mapping to ${selectedCodeIds.length} selected code${selectedCodeIds.length > 1 ? 's' : ''}`}
            </ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>Mapping:</Label>
                    <Select
                        className="text-dark"
                        options={filteredOptions}
                        onChange={(e) => {
                            setSelectedMapping(e?.value);
                        }}
                        onInputChange={handleInputChange}
                        filterOption={() => true}
                        isClearable={true}
                    />
                </FormGroup>
                {canWriteComments && (
                    <FormGroup className="mt-3">
                        <Label>{'Comments (Optional):'}</Label>
                        <Input
                            type="textarea"
                            rows="8"
                            value={commentsText}
                            maxLength={800}
                            onChange={(e) => setCommentsText(e.target.value)}
                        />
                    </FormGroup>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="info" onClick={closeModal} disabled={isBulkActionInProgress}>
                    Cancel
                </Button>
                <Button
                    color="success"
                    disabled={!selectedMapping || isBulkActionInProgress}
                    onClick={confirm}>
                    {isBulkActionInProgress ? (
                        <><Spinner size="sm" className="mr-1" /> Adding...</>
                    ) : (
                        `Add to ${selectedCodeIds.length} codes`
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default BulkAddMappingModal;
